import { useEffect, useRef, type RefObject } from "react";
import {
  emitAnalyticsEvent,
  type AnalyticsProperties,
} from "@/lib/analytics-core";

export function trackLanding(
  section: string,
  action: string,
  properties: AnalyticsProperties = {},
): void {
  emitAnalyticsEvent({
    eventType: `landing.${section}.${action}`,
    section,
    scope: "landing",
    properties,
  });
}

export function useLandingPageTracking(): void {
  useEffect(() => {
    // Enhance only the interacted link: scanning prerendered Suspense content
    // can mutate attributes before React hydrates those boundaries.
    const registrationAnchor = (event: Event) => {
      const anchor =
        event.target instanceof Element
          ? event.target.closest<HTMLAnchorElement>("a[href]")
          : null;
      if (!anchor) return null;
      const url = new URL(anchor.href, window.location.href);
      if (url.pathname !== "/register") return null;
      const query = new URLSearchParams(window.location.search);
      query.forEach((value, key) => {
        if (!url.searchParams.has(key)) url.searchParams.set(key, value);
      });
      if (anchor.href !== url.toString()) anchor.href = url.toString();
      return anchor;
    };
    const enhanceLink = (event: Event) => {
      registrationAnchor(event);
    };
    const onClick = (event: MouseEvent) => {
      const anchor = registrationAnchor(event);
      if (!anchor) return;
      trackLanding("conversion", "cta_click", {
        location:
          anchor.closest("section")?.id ||
          (anchor.closest("header") ? "header" : "sticky"),
      });
    };
    const enhancementEvents = ["pointerdown", "focusin", "contextmenu"];
    enhancementEvents.forEach((event) =>
      document.addEventListener(event, enhanceLink, true),
    );
    document.addEventListener("click", onClick, true);
    const startedAt = Date.now();
    trackLanding("page", "view", {
      page_path: window.location.pathname,
      referrer: document.referrer,
    });

    const milestones = [25, 50, 75, 100];
    const fired = new Set<number>();
    let frame = 0;

    const measure = () => {
      frame = 0;
      const available =
        document.documentElement.scrollHeight - window.innerHeight;
      const depth =
        available <= 0 ? 100 : Math.round((window.scrollY / available) * 100);
      for (const milestone of milestones) {
        if (depth >= milestone && !fired.has(milestone)) {
          fired.add(milestone);
          trackLanding("scroll", "depth", {
            scroll_depth: milestone,
            time_on_page: Math.round((Date.now() - startedAt) / 1000),
          });
        }
      }
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      enhancementEvents.forEach((event) =>
        document.removeEventListener(event, enhanceLink, true),
      );
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
}

/**
 * Observes the returned ref and emits `landing.{section}.view` once.
 *
 * `section` accepts null so a shared component can render on both a public
 * marketing surface and an in-app surface while only reporting into the
 * landing funnel on the former.
 */
export function useSectionView<T extends HTMLElement>(
  section: string | null,
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!section || !element || typeof IntersectionObserver === "undefined")
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          trackLanding(section, "view");
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [section]);

  return ref;
}
